// src/context/CartContext.jsx
import {track} from '../analytics/service';
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";

const CartContext = createContext(null);

// === Persistenza ===
export const CART_STORAGE_KEY = "alc_cart_v2";
const STORAGE_KEY = CART_STORAGE_KEY;
const TTL_DAYS = 2;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

// Evento globale: dispatchato da login/logout (anche fuori dall'albero React,
// es. authProvider.js) per garantire che il carrello non sopravviva mai a un
// cambio di identità sullo stesso browser.
export const AUTH_CHANGED_EVENT = "alc:auth-changed";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]); // { id, id_fornitore, descrizione|descrizione_capo, prezzo(EUR), qty, ... }
  const [isOpen, setIsOpen] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Metadati carrello
  const [cartMeta, setCartMeta] = useState({
    ritiro_at: null,
    consegna_at: null,
    ritiro_slot: null,
    consegna_slot: null,
    indirizzo: null,
    address_selection: null,
  });

  // Tariffa logistica (ritiro+consegna)
  const [logisticsFee, setLogisticsFee] = useState(2.0); // minimo configurato, salvo esenzione amministrativa

  const [logisticsFeeExempt, setLogisticsFeeExempt] = useState(false);

  // Promo “a codice” + sconto
  const [promo, setPromo] = useState(null); // { id, code, kind: 'percent'|'fixed', value, max_discount? }
  const [discountCents, setDiscountCents] = useState(0);

  // ✅ Promo automatiche (promoqta / paniere senza codice)
  const [autoPromos, setAutoPromos] = useState([]); // [{id, kind, label, discount}]
  const [autoDiscountCents, setAutoDiscountCents] = useState(0); // in cents
  // incrementato per forzare il ricalcolo (es. 409 AUTO_PROMO_CHANGED al pagamento)
  const [autoPromosVersion, setAutoPromosVersion] = useState(0);
  const refreshAutoPromos = useCallback(
    () => setAutoPromosVersion((v) => v + 1),
    [],
  );

  const resetMeta = useCallback(() => {
    setCartMeta((m) => ({
      ...m,
      ritiro_at: null,
      consegna_at: null,
      ritiro_slot: null,
      consegna_slot: null,
      // indirizzo resta
    }));
  }, []);

  const clearPromo = useCallback(() => {
    setPromo(null);
    setDiscountCents(0);
  }, []);

  const supplierId = useMemo(() => items[0]?.id_fornitore ?? null, [items]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const clearCart = useCallback(() => {
    setItems([]);
    resetMeta();
    clearPromo();
    setAutoPromos([]);
    setAutoDiscountCents(0);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, [resetMeta, clearPromo]);

  // Reset completo per cambio identità (login/logout): a differenza di
  // clearCart(), qui l'indirizzo NON viene preservato — appartiene alla
  // persona che sta uscendo, non deve essere ereditato da chi entra.
  const resetCartForAuthChange = useCallback(() => {
    setItems([]);
    setCartMeta({
      ritiro_at: null,
      consegna_at: null,
      ritiro_slot: null,
      consegna_slot: null,
      indirizzo: null,
      address_selection: null,
    });
    clearPromo();
    setAutoPromos([]);
    setAutoDiscountCents(0);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, [clearPromo]);

  useEffect(() => {
    const handler = () => resetCartForAuthChange();
    window.addEventListener(AUTH_CHANGED_EVENT, handler);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, handler);
  }, [resetCartForAuthChange]);

  // ====== DERIVED: quantità totale e conteggio righe ======
  const totalQty = useMemo(
    () =>
      items.reduce((sum, it) => sum + Math.max(1, Number(it.qty ?? 1) || 1), 0),
    [items],
  );
  const itemCount = useMemo(() => items.length, [items]);
  const hasItems = totalQty > 0;

  // ====== TOTALE MERCE / FEE / SCONTO / TOTALE ======
  const subtotalCents = useMemo(() => {
    return items.reduce((acc, it) => {
      const qty = Math.max(1, Number(it.qty ?? 1) || 1);
      const unitCents = Math.round((Number(it.prezzo) || 0) * 100);
      return acc + unitCents * qty;
    }, 0);
  }, [items]);

  const feeCents = useMemo(
    () => logisticsFeeExempt ? 0 : Math.round(Math.max(2, Number(logisticsFee || 0)) * 100),
    [logisticsFee, logisticsFeeExempt],
  );

  const effectiveDiscountCents = useMemo(
    () => Math.min(discountCents + autoDiscountCents || 0, subtotalCents),
    [discountCents, autoDiscountCents, subtotalCents],
  );

  const totalCents = useMemo(() => {
    const base = Math.max(0, subtotalCents - effectiveDiscountCents);
    return base + feeCents;
  }, [subtotalCents, effectiveDiscountCents, feeCents]);

  // ---------- addItem / addItems (single-supplier) ----------
  const addItem = useCallback(
    (item, { allowReplace = false } = {}) => {
      if (
        items.length > 0 &&
        supplierId !== null &&
        item.id_fornitore !== supplierId
      ) {
        if (!allowReplace) {
          return {
            ok: false,
            reason: "DIFFERENT_SUPPLIER",
            supplierInCart: supplierId,
          };
        }
        setItems([{ ...item, qty: Math.max(1, Number(item.qty ?? 1) || 1) }]);
        track('add_to_cart',{catalog_id:Number(item.id_servizio),quantity:Math.max(1,Number(item.qty)||1),currency:'EUR',value:Number(item.prezzo)*Math.max(1,Number(item.qty)||1)});
        resetMeta();
        clearPromo();
        return { ok: true, replaced: true };
      }

      setItems((prev) => {
        const idx = prev.findIndex((it) => it.id === item.id);
        if (idx >= 0) {
          const clone = [...prev];
          const nowQty = Math.max(1, Number(clone[idx].qty ?? 1) || 1);
          const inc = Math.max(1, Number(item.qty ?? 1) || 1);
          clone[idx] = { ...clone[idx], qty: nowQty + inc };
          return clone;
        }
        return [
          ...prev,
          { ...item, qty: Math.max(1, Number(item.qty ?? 1) || 1) },
        ];
      });

      track('add_to_cart',{catalog_id:Number(item.id_servizio),quantity:Math.max(1,Number(item.qty)||1),currency:'EUR',value:Number(item.prezzo)*Math.max(1,Number(item.qty)||1)});
      return { ok: true, replaced: false };
    },
    [items, supplierId, resetMeta, clearPromo],
  );

  const addItems = useCallback(
    (incomingItems, { allowReplace = false } = {}) => {
      if (!Array.isArray(incomingItems) || incomingItems.length === 0) {
        return { ok: true, replaced: false };
      }
      const newSupplier = incomingItems[0].id_fornitore;
      if (incomingItems.some((it) => it.id_fornitore !== newSupplier)) {
        return { ok: false, reason: "MIXED_SUPPLIERS_IN_PAYLOAD" };
      }

      if (
        items.length > 0 &&
        supplierId !== null &&
        newSupplier !== supplierId
      ) {
        if (!allowReplace) {
          return {
            ok: false,
            reason: "DIFFERENT_SUPPLIER",
            supplierInCart: supplierId,
          };
        }
        setItems(
          incomingItems.map((it) => ({
            ...it,
            qty: Math.max(1, Number(it.qty ?? 1) || 1),
          })),
        );
        resetMeta();
        clearPromo();
        track('add_to_cart',{items:incomingItems});
        return { ok: true, replaced: true };
      }

      setItems((prev) => {
        const map = new Map(prev.map((it) => [it.id, { ...it }]));
        for (const it of incomingItems) {
          const q = Math.max(1, Number(it.qty ?? 1) || 1);
          if (map.has(it.id)) {
            const cur = map.get(it.id);
            const curQ = Math.max(1, Number(cur.qty ?? 1) || 1);
            map.set(it.id, { ...cur, qty: curQ + q });
          } else {
            map.set(it.id, { ...it, qty: q });
          }
        }
        return Array.from(map.values());
      });

      track('add_to_cart',{items:incomingItems});
      return { ok: true, replaced: false };
    },
    [items, supplierId, resetMeta, clearPromo],
  );

  // ---------- utility base ----------
  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const changeQty = useCallback((id, newQty) => {
    const q = Math.max(0, Number(newQty) || 0);
    const item=items.find(it=>it.id===id);
    if (item && q>Number(item.qty)) track('add_to_cart',{catalog_id:Number(item.id_servizio),quantity:q-Number(item.qty)});
    setItems((prev) => {
      if (q <= 0) return prev.filter((it) => it.id !== id);
      return prev.map((it) => (it.id === id ? { ...it, qty: q } : it));
    });
  }, [items]);

  const bumpQty = useCallback((id, delta) => {
    const item=items.find(it=>it.id===id);
    if (item && Number(delta)>0) track('add_to_cart',{catalog_id:Number(item.id_servizio),quantity:Number(delta)});
    setItems((prev) =>
      prev.flatMap((it) => {
        if (it.id !== id) return [it];
        const next = (Number(it.qty) || 0) + (Number(delta) || 0);
        if (next <= 0) return []; // rimuovi
        return [{ ...it, qty: next }];
      }),
    );
  }, [items]);

  const updateItem = useCallback((id, patch) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...patch, id_fornitore: it.id_fornitore } : it)),
    );
  }, []);

  // ---------- promo a codice ----------
  const applyPromo = useCallback(
    async (code) => {
      const clean = (code || "").trim();
      if (!clean) throw new Error("Inserisci un codice");

      const token = localStorage.getItem("token");
      const payload = { code: clean, supplierId,
          items: items.map(it => ({ service_id: it.id_servizio ?? it.service_id ?? it.servizio_id,
            qty: Number(it.qty ?? 1), unit_price: Number(it.prezzo || 0) })) };

      const r = await fetch(`${API_BASE}/api/cart/promo/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      const js = await r.json().catch(() => ({}));
      if (!r.ok || !js.ok) {
        throw new Error(js.error || "Codice non valido");
      }

      setPromo(js.promo || null);
      setDiscountCents(Math.round(Number(js.discount || 0) * 100));
      return { ok: true };
    },
    [supplierId, items, API_BASE],
  );

  const removePromo = useCallback(() => {
    clearPromo();
    return { ok: true };
  }, [clearPromo]);

  // Ricalcolo promo a codice
  useEffect(() => {
    if (!promo?.code) {
      if (discountCents !== 0) setDiscountCents(0);
      return;
    }
    if (subtotalCents <= 0) {
      setDiscountCents(0);
      setPromo(null);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem("token");
        const payload = { code: promo.code, supplierId,
          items: items.map(it => ({ service_id: it.id_servizio ?? it.service_id ?? it.servizio_id,
            qty: Number(it.qty ?? 1), unit_price: Number(it.prezzo || 0) })) };

        const r = await fetch(`${API_BASE}/api/cart/promo/validate`, {
          signal: controller.signal,
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(payload),
        });
        const js = await r.json().catch(() => ({}));

        if (!r.ok || !js.ok) {
          setPromo(null);
          setDiscountCents(0);
          return;
        }

        const nextDiscountCents = Math.round(Number(js.discount || 0) * 100);
        setDiscountCents(nextDiscountCents);
        if (js.promo) setPromo(js.promo);
      } catch {
        // noop
      }
    }, 200);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [promo?.code, supplierId, subtotalCents, items, API_BASE, discountCents]);

  // ✅ Auto-promozioni (promoqta / paniere con requires_code=false)
  useEffect(() => {
    if (!items.length || !supplierId) {
      setAutoPromos([]);
      setAutoDiscountCents(0);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem("token");
        const payload = {
          supplierId,
          items: items.map((it) => ({
            service_id:
              it.id_servizio ?? it.service_id ?? it.servizio_id ?? null,
            qty: Math.max(1, Number(it.qty ?? 1) || 1),
            unit_price: Number(it.prezzo || 0), // EUR
          })),
        };

        const r = await fetch(`${API_BASE}/api/cart/promo/auto`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        const js = await r.json().catch(() => ({}));
        if (!r.ok || !js.ok) {
          setAutoPromos([]);
          setAutoDiscountCents(0);
          return;
        }

        setAutoPromos(Array.isArray(js.promos) ? js.promos : []);
        // js.discount è in EUR
        setAutoDiscountCents(Math.round(Number(js.discount || 0) * 100));
      } catch {
        if (!controller.signal.aborted) {
          setAutoPromos([]);
          setAutoDiscountCents(0);
        }
      }
    }, 150);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [items, supplierId, API_BASE, autoPromosVersion]);

  // ---------- NUOVE utility sicure per i pulsanti + / − ----------
  const getQty = useCallback(
    (id) => items.find((it) => it.id === id)?.qty || 0,
    [items],
  );

  const incrementItem = useCallback((id) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, qty: (Number(it.qty) || 1) + 1 } : it,
      ),
    );
  }, []);

  const decrementItem = useCallback((id) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.id === id);
      if (idx < 0) return prev;
      const cur = Number(prev[idx].qty) || 1;
      if (cur <= 1) return prev.filter((it) => it.id !== id);
      const clone = [...prev];
      clone[idx] = { ...clone[idx], qty: cur - 1 };
      return clone;
    });
  }, []);

  const normalizeServiceToItem = useCallback(
    (s, qty = 1) => {
      const id =
        s?.id ?? s?.id_servizio ?? s?.servizio_id ?? s?.service_id ?? null;
      const forn =
        s?.id_fornitore ??
        s?.fornitore_id ??
        s?.fornitore?.id ??
        supplierId ??
        null;

      return {
        id,
        id_fornitore: forn,
        descrizione:
          s?.descrizione ??
          s?.descrizione_capo ??
          s?.nome ??
          s?.titolo ??
          "Articolo",
        prezzo: Number(s?.prezzo ?? s?.price ?? s?.unit_price ?? 0),
        qty: Math.max(1, Number(qty) || 1),
        _raw: s,
      };
    },
    [supplierId],
  );

  const incByService = useCallback(
    (service, { allowReplace = false } = {}) => {
      const item = normalizeServiceToItem(service, 1);
      if (!item.id) return { ok: false, reason: "MISSING_ID" };
      if (!item.id_fornitore && supplierId != null) {
        item.id_fornitore = supplierId;
      }
      return addItem(item, { allowReplace });
    },
    [addItem, normalizeServiceToItem, supplierId],
  );

  const decByService = useCallback(
    (service) => {
      const id =
        service?.id ??
        service?.id_servizio ??
        service?.servizio_id ??
        service?.service_id ??
        null;
      if (!id) return { ok: false, reason: "MISSING_ID" };
      decrementItem(id);
      return { ok: true };
    },
    [decrementItem],
  );

  // ---------- helper totali ----------
  const toEuro = useCallback(
    (cents) => (Number(cents || 0) / 100).toFixed(2),
    [],
  );

  const totals = useMemo(
    () => ({
      subtotalCents,
      discountCents: effectiveDiscountCents,
      feeCents,
      totalCents,
      subtotal: toEuro(subtotalCents),
      discount: toEuro(effectiveDiscountCents),
      fee: toEuro(feeCents),
      total: toEuro(totalCents),
    }),
    [subtotalCents, effectiveDiscountCents, feeCents, totalCents, toEuro],
  );

  // ===== Hydrate from localStorage (con TTL) =====
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);

      // TTL
      if (parsed?.savedAt && Date.now() - parsed.savedAt > TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      if (Array.isArray(parsed.items)) {
        const supplierIds = new Set(parsed.items.map(it => Number(it.id_fornitore)));
        if (supplierIds.size > 1) { localStorage.removeItem(STORAGE_KEY); return; }
        setItems(parsed.items);
      }
      if (parsed.cartMeta && typeof parsed.cartMeta === "object") {
        setCartMeta((m) => ({ ...m, ...parsed.cartMeta }));
      }
      if (parsed.logisticsFee != null) {
        setLogisticsFee(Math.max(2, Number(parsed.logisticsFee) || 2));
        setLogisticsFeeExempt(parsed.logisticsFeeExempt === true);
      }
      if (parsed.promo) setPromo(parsed.promo);
      if (parsed.discountCents != null)
        setDiscountCents(Number(parsed.discountCents) || 0);

      if (Array.isArray(parsed.autoPromos)) setAutoPromos(parsed.autoPromos);
      if (parsed.autoDiscountCents != null)
        setAutoDiscountCents(Number(parsed.autoDiscountCents) || 0);
    } catch {
      // ignore
    }
  }, []);

  // ===== Persist snapshot =====
  useEffect(() => {
    try {
      const snapshot = {
        items,
        cartMeta,
        logisticsFee,
        logisticsFeeExempt,
        promo,
        discountCents,
        autoPromos,
        autoDiscountCents,
        savedAt: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // ignore
    }
  }, [
    items,
    cartMeta,
    logisticsFee,
    logisticsFeeExempt,
    promo,
    discountCents,
    autoPromos,
    autoDiscountCents,
  ]);

  const value = useMemo(
    () => ({
      // stato
      items,
      supplierId,
      isOpen,
      cartMeta,
      logisticsFee,
      logisticsFeeExempt,
      promo,

      // derived
      totalQty,
      itemCount,
      hasItems,

      // promo automatiche
      autoPromos,
      autoDiscountCents,

      // open/close
      openCart,
      closeCart,

      // items
      addItem,
      addItems,
      removeItem,
      clearCart,
      changeQty,
      bumpQty,
      updateItem,

      // nuove API per + / −
      getQty,
      incrementItem,
      decrementItem,
      incByService,
      decByService,

      // fees/promo
      setLogisticsFee: (fee, { exempt = false } = {}) => {
        setLogisticsFee(Math.max(2, Number(fee || 0)));
        setLogisticsFeeExempt(exempt === true);
      },
      applyPromo,
      removePromo,
      refreshAutoPromos,

      // totali
      totalCents, // compat legacy
      totals, // breakdown comodo
      setCartMeta,
    }),
    [
      items,
      supplierId,
      isOpen,
      cartMeta,
      logisticsFee,
      logisticsFeeExempt,
      promo,
      totalQty,
      itemCount,
      hasItems,
      autoPromos,
      autoDiscountCents,
      openCart,
      closeCart,
      addItem,
      addItems,
      removeItem,
      clearCart,
      changeQty,
      bumpQty,
      updateItem,
      getQty,
      incrementItem,
      decrementItem,
      incByService,
      decByService,
      totalCents,
      totals,
      setCartMeta,
      applyPromo,
      removePromo,
      refreshAutoPromos,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}
